import { Box, Button, Checkbox, MenuItem, Select } from '@mui/material';
import React, { useState } from 'react';
import { quantityValueText, volumeValueText, weightValueText } from 'src/components/dispatch/metricText';

import { ShipperBreakdownRow } from './shipperBreakdown';
import { DetailPanelMoveTarget } from './types';

interface ShipperBreakdownSectionProps {
  rows: ShipperBreakdownRow[];
  /** 選択 UI を出すときだけ渡す。移動先候補は「他車両へ配車」と同じものを使う */
  moveTargets?: DetailPanelMoveTarget[];
  /** 未指定なら読み取り専用。選択した荷主の legRun を移動先の行へ移す */
  onMoveShippers?: (legRunIds: number[], targetRowId: string) => void;
}

/**
 * 地点の数量を荷主ごとに分けて出し、荷主を選んで他車両へ移せるようにする
 * (logposeTech/log_web_issues#326)。
 *
 * Why 選択 state をここに閉じ込める: 開いているポップアップの中だけで完結し、
 * サーバにも他の画面にも出ていかない。呼び出し側の hook に置くと scope 破棄と
 * selection 同期の対象が 1 つ増えるだけで得るものが無い。
 * 別のカードへ切り替わったときの破棄は、呼び出し側が `key` を変えて行う
 * (DispatchDetailPanel の breakdownKey)。useEffect で消すより、消し忘れる分岐が
 * 原理的に作れない。
 */
export function ShipperBreakdownSection({ rows, moveTargets, onMoveShippers }: ShipperBreakdownSectionProps) {
  /**
   * 動かす legRun が引ける行だけを選ばせる。カード単位の規則（legRunIds が空なら
   * 操作を出さない・DetailPanelShell の canMoveOrReset）を行単位にも当てはめる。
   *
   * Why not 実行時にまとめて弾く: 空の行と中身のある行を一緒に選ぶと、
   * 「2社 を移動」と出しながら 1 社ぶんしか動かない（黙って一部だけ動く）。
   * 選ばせない側で止めれば、表示と実際に動く範囲が食い違わない。
   */
  const canMoveRow = (row: ShipperBreakdownRow) => row.legRunIds.length > 0;
  const movableRows = rows.filter(canMoveRow);
  const selectable = onMoveShippers != null && (moveTargets?.length ?? 0) > 0 && movableRows.length > 0;
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [targetRowId, setTargetRowId] = useState('');

  const toggle = (shipperName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(shipperName)) next.add(shipperName);
      return next;
    });
  };

  /**
   * 選択は荷主名で持つが、動かす対象は「いま出ている行」から引き直す。
   * 表示中でもデータ再取得で内訳は入れ替わる (resolveSelectionFromLatest) ので、
   * 消えた荷主のぶんを掴んだまま実行しないようにする。
   */
  const selectedRows = movableRows.filter((row) => selected.has(row.shipperName));
  const allSelected = movableRows.length > 0 && movableRows.every((row) => selected.has(row.shipperName));
  // 同じ理由で、移動先も「いまの候補にあるか」を毎回見る
  const targetIsValid = (moveTargets ?? []).some((t) => t.rowId === targetRowId);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Box sx={{ fontSize: 12, color: 'hsl(var(--foreground) / 0.7)' }}>
          {`${rows.length} 社`}
        </Box>
        {selectable && (
          <Button
            size="small"
            onClick={() => setSelected(allSelected ? new Set() : new Set(movableRows.map((r) => r.shipperName)))}
            sx={{ ml: 'auto', minWidth: 0, p: 0, fontSize: 11, textTransform: 'none' }}
          >
            {allSelected ? '全解除' : '全選択'}
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {rows.map((row) => {
          const rowSelectable = selectable && canMoveRow(row);
          const checked = selected.has(row.shipperName);
          return (
            <Box
              key={row.shipperName}
              onClick={rowSelectable ? () => toggle(row.shipperName) : undefined}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                borderRadius: '4px',
                px: selectable ? 0.5 : 0,
                cursor: rowSelectable ? 'pointer' : 'default',
                bgcolor: checked ? 'hsl(var(--primary) / 0.08)' : 'transparent',
              }}
            >
              {selectable && (
                <Checkbox
                  size="small"
                  checked={checked}
                  disabled={!rowSelectable}
                  title={rowSelectable ? undefined : 'この荷主は移動できません'}
                  onChange={() => toggle(row.shipperName)}
                  // 詳細パネルは Popover / 背景クリックで閉じる器の中にある。
                  // 行と器の両方へ抜けないよう、境界はこの 1 か所で切る
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.25 }}
                  inputProps={{ 'aria-label': `${row.shipperName} を選択` }}
                />
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ fontWeight: 500, fontSize: 13 }}>{row.shipperName}</Box>
                <Box sx={{ fontSize: 12, color: 'hsl(var(--foreground) / 0.7)', fontVariantNumeric: 'tabular-nums' }}>
                  {`${quantityValueText(row.itemCount)}cs / ${weightValueText(row.weightKg)}kg / ${volumeValueText(row.volumeM3)}m³ / 伝票${row.invoiceCount}`}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {selectable && selectedRows.length > 0 && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.75,
            borderRadius: '8px',
            border: '1px solid hsl(var(--primary) / 0.35)',
            bgcolor: 'hsl(var(--primary) / 0.06)',
          }}
        >
          <Box sx={{ fontSize: 11, fontWeight: 600, color: 'hsl(var(--primary))' }}>
            {`${selectedRows.length}社 / ${quantityValueText(selectedRows.reduce((n, r) => n + r.itemCount, 0))}cs を移動`}
          </Box>
          <Select
            size="small"
            displayEmpty
            value={targetIsValid ? targetRowId : ''}
            onChange={(e) => setTargetRowId(e.target.value)}
            inputProps={{ 'aria-label': '移動先の車両を選択' }}
            sx={{ flex: 1, minWidth: 150, fontSize: 12, '& .MuiSelect-select': { py: 0.5, fontSize: 12 } }}
          >
            <MenuItem value="" sx={{ fontSize: 12 }}>移動先の車両を選択</MenuItem>
            {(moveTargets ?? []).map((target) => (
              <MenuItem key={target.rowId} value={target.rowId} sx={{ fontSize: 12 }}>
                {target.label}
                <Box component="span" sx={{ ml: 0.75, fontSize: 11, color: 'text.secondary' }}>
                  {target.subLabel}
                </Box>
              </MenuItem>
            ))}
          </Select>
          <Button
            size="small"
            variant="contained"
            disabled={!targetIsValid}
            onClick={() => {
              onMoveShippers?.(selectedRows.flatMap((r) => r.legRunIds), targetRowId);
            }}
            sx={{ height: 28, fontSize: 11, textTransform: 'none' }}
          >
            実行
          </Button>
        </Box>
      )}
    </Box>
  );
}
